import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";
import * as demoStore from "@/demo/demoStore";
import type { ID } from "@vlab-corporation/vlab-pilates-service-interface/types";

export interface ClassCompletionDocument {
  classId: ID;
  journalWritten: boolean;
  journalId?: string;
  isNoShow?: boolean;
  updatedAt: unknown;
}

function completionRef(ownerUid: string, centerId: number, classId: ID) {
  return doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "classCompletions",
    classId,
  );
}

function completionsCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "classCompletions",
  );
}

export async function getClassCompletion(
  ownerUid: string,
  centerId: number,
  classId: ID,
): Promise<ClassCompletionDocument> {
  if (isDemoMode()) return demoStore.getClassCompletion(centerId, classId);
  const snap = await getDoc(completionRef(ownerUid, centerId, classId));
  if (!snap.exists()) {
    return { classId, journalWritten: false, updatedAt: null };
  }
  return snap.data() as ClassCompletionDocument;
}

export async function getClassCompletionsBatch(
  ownerUid: string,
  centerId: number,
  classIds: ID[],
): Promise<Map<ID, ClassCompletionDocument>> {
  const map = new Map<ID, ClassCompletionDocument>();
  if (classIds.length === 0) return map;

  if (isDemoMode()) {
    for (const id of classIds) {
      map.set(id, demoStore.getClassCompletion(centerId, id));
    }
    return map;
  }

  // Firestore `in` queries support max 30 items per query
  const chunks: ID[][] = [];
  for (let i = 0; i < classIds.length; i += 30) {
    chunks.push(classIds.slice(i, i + 30));
  }

  const col = completionsCollection(ownerUid, centerId);
  const results = await Promise.all(
    chunks.map((chunk) => getDocs(query(col, where("classId", "in", chunk)))),
  );

  for (const snap of results) {
    for (const d of snap.docs) {
      const data = d.data() as ClassCompletionDocument;
      map.set(data.classId, data);
    }
  }

  return map;
}

export async function updateClassCompletion(
  ownerUid: string,
  centerId: number,
  classId: ID,
  journalWritten: boolean,
  journalId?: string,
  isNoShow?: boolean,
): Promise<void> {
  if (isDemoMode())
    return demoStore.updateClassCompletion(centerId, classId, journalWritten, journalId);
  await setDoc(
    completionRef(ownerUid, centerId, classId),
    {
      classId,
      journalWritten,
      ...(journalId ? { journalId } : {}),
      ...(isNoShow !== undefined ? { isNoShow } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * 그룹 수업의 journalWritten 상태를 수강생별 일지 작성 현황에 따라 업데이트한다.
 *
 * 판정 기준:
 * - journalCount === activeTrainees → journalWritten = true (전원 일지 작성 완료)
 * - journalCount < activeTrainees  → journalWritten = false (일부 미작성)
 *
 * 사용 시나리오:
 * JournalPage에서 그룹 수업 일지 저장 성공 후 이 함수를 호출하여
 * classCompletions의 journalWritten 상태를 갱신한다.
 *
 * TODO: JournalPage.tsx에서 saveJournal/updateJournal 성공 후
 *       이 함수를 호출하도록 연동 필요.
 *       연동 예시:
 *         const journals = await getJournalsByClassId(ownerUid, centerId, classId);
 *         const activeTrainees = schedule.bookings.filter(b => b.status !== "cancel").length;
 *         await updateGroupClassJournalWritten(ownerUid, centerId, classId, journals.length, activeTrainees);
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param classId - 수업 ID
 * @param journalCount - 해당 수업에 저장된 일지 수 (getJournalsByClassId로 조회)
 * @param activeTrainees - 해당 수업의 active 수강생 수 (cancel 상태 제외)
 */
export async function updateGroupClassJournalWritten(
  ownerUid: string,
  centerId: number,
  classId: ID,
  journalCount: number,
  activeTrainees: number,
): Promise<void> {
  // active 수강생이 없으면 판정 불가 — 업데이트하지 않는다
  if (activeTrainees <= 0) return;

  const journalWritten = journalCount >= activeTrainees;

  if (isDemoMode()) {
    return demoStore.updateClassCompletion(centerId, classId, journalWritten);
  }

  const ref = completionRef(ownerUid, centerId, classId);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    // 문서가 이미 존재하는 경우 journalWritten 필드만 갱신
    await updateDoc(ref, {
      journalWritten,
      updatedAt: serverTimestamp(),
    });
  } else {
    // 문서가 없는 경우 신규 생성
    await setDoc(ref, {
      classId,
      journalWritten,
      updatedAt: serverTimestamp(),
    });
  }
}
